import React, { Component, createRef } from 'react';
import { fabric } from 'fabric';
import { GOOGLE_FONTS, loadGoogleFont, loadFontsUsedInCanvas } from './fonts';
import { createHistory } from './history';
import { createViewport } from './viewport';
import { registerFeatures } from './features';
import {
  BG_QUALITY, DEFAULT_BG_QUALITY, preloadModel, removeImageBackground,
} from './backgroundRemoval';
import { upscaleImageElement, inpaint } from './imageOps';
import './DesignEditor.css';

// ===========================================================================
//  EDITOR API SEAM  (this.editor) — the contract feature modules plug into.
//  Passed to registerFeatures(editor) in componentDidMount, and to every
//  addToolButton/addPanelSection/registerCommand callback. Call these VERBATIM.
//
//  editor = {
//    canvas,                       // fabric.Canvas (the live instance)
//    fabric,                       // the fabric namespace (convenience)
//    getArtboard(): {width,height} // logical design size (the exported region)
//    setArtboard(w, h),            // resize the artboard (debounced elsewhere)
//    getArtboardBg(): string|null, // artboard background color ('' -> null)
//    isExporting(): boolean,       // true only during export render (skip overlays)
//    isEditingText(): boolean,     // an IText/Textbox is in edit mode
//    isSelectionEnabled(): boolean,
//
//    pushHistory(label?),                 // snapshot NOW (route every mutation here)
//    pushHistoryDebounced(label?, ms?),   // one snapshot per gesture (sliders/typing)
//    undo(), redo(),
//
//    getActive(): fabric.Object|null,     // canvas.getActiveObject()
//    getObjects(): fabric.Object[],
//    replaceImage(obj, src, opts, cb),    // swap an image keeping transform/z/selection
//                                         //  opts: {name?, label?, preserveDisplaySize=true}
//    requestRender(),                     // canvas.requestRenderAll()
//    refreshLayers(),                     // rebuild the layers list from canvas
//    toast(msg, type),                    // 'error' | 'success' | 'info' (Romanian copy)
//    progress(msg | null),                // long-op progress line (null clears)
//
//    enterMode(name, handlers), exitMode(name?), isMode(name), getMode(),
//        // ONE owner per mode (crop, mask, ...). handlers: {onExit?}. Entering a
//        // new mode auto-exits the current one. Escape (stage 1) exits the mode.
//
//    registerCommand(keySpec, fn, opts) -> unregister()
//        // keySpec: 'mod+g' | 'shift+arrowup' | ['mod+=','mod+plus'] | 'r'
//        // fn(editor, event). opts.when(): gate. FOUNDATION owns keyboard dispatch;
//        // features never attach their own window keydown — register here.
//    addToolButton(def) -> remove()      // def {id,title,icon,onClick,isActive?()}
//    addPanelSection(def) -> remove()    // def {id,title,when?(sel),render(editor),order?}
//    registerSerializationProps(props),  // extra toObject props to persist (lock,...)
//    getSerializationProps(): string[],
//
//    screenToCanvas({x,y}): {x,y},       // client px -> artboard/object space
//    canvasToScreen({x,y}): {x,y},       // object space -> client px
//    viewport,                           // zoom/pan controller (zoomIn/fit/... )
//  }
// ===========================================================================

const MAX_EDIT_DIM = 2560;

const PRESETS = [
  { label: 'Logo (400x400)', w: 400, h: 400 },
  { label: 'Card calendar (400x400)', w: 400, h: 400 },
  { label: 'Banner lat (1200x630)', w: 1200, h: 630 },
  { label: 'Previzualizare social (1200x630)', w: 1200, h: 630 },
  { label: 'Story (1080x1920)', w: 1080, h: 1920 },
  { label: 'Personalizat', w: null, h: null },
];

// Base props persisted in every snapshot / saved design so lock + visibility +
// layer names survive undo and reload. Feature modules append via
// registerSerializationProps().
const BASE_SERIALIZATION_PROPS = [
  'name', 'src', 'crossOrigin', 'selectable', 'evented', 'visible',
  'lockMovementX', 'lockMovementY', 'lockScalingX', 'lockScalingY', 'lockRotation',
];

class DesignEditor extends Component {
  constructor(props) {
    super(props);
    this.canvasRef = createRef();
    this.canvasAreaRef = createRef();
    this.editorRef = createRef();
    this.fileInputRef = createRef();
    this.fontPickerRef = createRef();

    const preset = props.preset || PRESETS[0];
    this.state = {
      canvasWidth: preset.w || 400,
      canvasHeight: preset.h || 400,
      activeTool: 'select',
      activeObject: null,
      activeType: null,
      fillColor: '#ffffff',
      strokeColor: '#000000',
      fontSize: 32,
      fontFamily: 'Arial',
      fontBold: false,
      fontItalic: false,
      fontSearch: '',
      fontDropdownOpen: false,
      opacity: 1,
      zoomPct: 100,
      showEventImages: false,
      bgColor: '',
      removingBg: false,
      removingBgProgress: null,
      bgQuality: DEFAULT_BG_QUALITY,
      upscaling: false,
      showUpscaleMenu: false,
      magicEraseMode: false,
      magicEraseApplying: false,
      magicEraseBrushSize: 30,
      shadowEnabled: false,
      shadowColor: '#000000',
      shadowBlur: 10,
      shadowOffsetX: 4,
      shadowOffsetY: 4,
      layers: [],
      toast: null,
      canUndo: false,
      canRedo: false,
      // feature-registered surfaces
      extraToolButtons: [],
      extraPanelSections: [],
    };

    // non-state internals
    this._commands = [];
    this._mode = null;
    this._exporting = false;
    this._serializationProps = BASE_SERIALIZATION_PROPS.slice();
    this._historyDebounceTimer = null;
    this._resizeDebounceTimer = null;
    this._clipboard = null;
  }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================
  componentDidMount() {
    this.initCanvas();
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('paste', this.handlePaste);
    // Preload the bg-removal model during editing so the first click is instant.
    preloadModel(this.state.bgQuality);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('paste', this.handlePaste);
    clearTimeout(this._toastTimer);
    clearTimeout(this._historyDebounceTimer);
    clearTimeout(this._resizeDebounceTimer);
    if (this._editorEl) {
      this._editorEl.removeEventListener('dragover', this.handleDragOver);
      this._editorEl.removeEventListener('drop', this.handleDrop);
    }
    if (this.viewport) this.viewport.detach();
    if (this.canvas) this.canvas.dispose();
  }

  // ===========================================================================
  // Canvas init + editor API assembly
  // ===========================================================================
  initCanvas = () => {
    const areaEl = this.canvasAreaRef.current;
    const w = (areaEl && areaEl.clientWidth) || 800;
    const h = (areaEl && areaEl.clientHeight) || 600;

    this.canvas = new fabric.Canvas(this.canvasRef.current, {
      width: w,
      height: h,
      backgroundColor: null,
      preserveObjectStacking: true,
      selection: true,
      fireRightClick: false,
      stopContextMenu: false,
    });

    // -- build the editor API seam ------------------------------------------
    this.history = createHistory(this.canvas, {
      cap: 40,
      serializationProps: () => this._serializationProps,
    });

    this.editor = {
      canvas: this.canvas,
      fabric,
      getArtboard: () => ({ width: this.state.canvasWidth, height: this.state.canvasHeight }),
      setArtboard: (aw, ah) => this.resizeCanvas(aw, ah),
      getArtboardBg: () => this.state.bgColor || null,
      isExporting: () => this._exporting,
      isEditingText: () => {
        const o = this.canvas.getActiveObject();
        return !!(o && o.isEditing);
      },
      isSelectionEnabled: () => this.state.activeTool === 'select',
      pushHistory: (label) => this.pushHistory(label),
      pushHistoryDebounced: (label, ms) => this.pushHistoryDebounced(label, ms),
      undo: this.undo,
      redo: this.redo,
      getActive: () => this.canvas.getActiveObject(),
      getObjects: () => this.canvas.getObjects(),
      replaceImage: this.replaceImage,
      requestRender: () => this.canvas.requestRenderAll(),
      refreshLayers: this.refreshLayers,
      toast: this.toast,
      progress: (msg) => this.setState({ removingBgProgress: msg }),
      enterMode: this.enterMode,
      exitMode: this.exitMode,
      isMode: (name) => !!this._mode && this._mode.name === name,
      getMode: () => (this._mode ? this._mode.name : null),
      registerCommand: this.registerCommand,
      addToolButton: this.addToolButton,
      addPanelSection: this.addPanelSection,
      registerSerializationProps: this.registerSerializationProps,
      getSerializationProps: () => this._serializationProps,
      screenToCanvas: (pt) => this.viewport.screenToCanvas(pt),
      canvasToScreen: (pt) => this.viewport.canvasToScreen(pt),
      // The canvas-area DOM ref. viewport.js reads editor.canvasAreaRef.current
      // for sizing/coord math (and guides.js falls back to it) — expose it here.
      canvasAreaRef: this.canvasAreaRef,
      // -- seam additions for the export-dialog feature (Group H) --------------
      // exportImage: proven crop path (identity viewport, artboard-rect, no
      // pasteboard/outline) returning a data URL. emitExport: hand a data URL +
      // the current designJson to the host's onExport (the "Foloseste" path).
      exportImage: (format = 'png', multiplier = 1) => this.exportAsImage(format, multiplier),
      emitExport: (dataURL) => { if (this.props.onExport) this.props.onExport(dataURL, this.exportDesignJson()); },
      onViewportChange: () => this.setState({ zoomPct: Math.round(this.canvas.getZoom() * 100) }),
    };

    this.viewport = createViewport(this.editor);
    this.editor.viewport = this.viewport;

    // Artboard paper + outline are painted by the viewport render hooks.
    this.canvas.on('before:render', this.viewport.beforeRender);
    this.canvas.on('after:render', this.viewport.afterRender);

    // Canvas event wiring — FOUNDATION owns these. object:moving is intentionally
    // NOT bound here (the guides/snapping feature owns it).
    this.canvas.on('selection:created', this.handleSelection);
    this.canvas.on('selection:updated', this.handleSelection);
    this.canvas.on('selection:cleared', () => this.setState({ activeObject: null, activeType: null }));
    this.canvas.on('object:modified', () => { this.pushHistory('Modificare'); this.refreshLayers(); });
    this.canvas.on('object:added', () => this.refreshLayers());
    this.canvas.on('object:removed', () => this.refreshLayers());
    // Only the foundation free-draw tool snapshots strokes here. While a canvas
    // MODE owns drawing (magic erase, mask refine, ...) it manages its own strokes
    // and history — suppress this handler so temporary overlay paths (e.g. the red
    // magic-erase brush) never get baked into an undo snapshot or dirty the design.
    this.canvas.on('path:created', () => { if (this._mode) return; this.pushHistory('Desen'); });
    // One snapshot per text edit session.
    this.canvas.on('text:editing:exited', () => this.pushHistory('Editare text'));

    this.viewport.attach();
    this._registerCoreCommands();
    registerFeatures(this.editor);

    // Drag & drop on this editor instance's root.
    this._editorEl = this.editorRef.current;
    if (this._editorEl) {
      this._editorEl.addEventListener('dragover', this.handleDragOver);
      this._editorEl.addEventListener('drop', this.handleDrop);
    }

    this._loadInitialContent();
  };

  _loadInitialContent = () => {
    if (this.props.designJson) {
      try {
        const parsed = this._prepareJsonForLoad(this.props.designJson);
        // Restore the artboard size persisted by exportDesignJson. Without this
        // the re-opened design keeps the preset default (400x400) and the export
        // crops to the wrong region — corrupting the saved full-res image.
        const dims = this._artboardDimsFromDesign(parsed);
        const finishLoad = () => {
          this.canvas.loadFromJSON(parsed, () => {
            this._absorbBackground(parsed);
            // Legacy designs saved before artboard dims were persisted: size the
            // artboard to the loaded content so products (sizeToImage) don't crop
            // to the default. Calendar (no sizeToImage) keeps its 400x400 default.
            if (!dims && this.props.sizeToImage) this._sizeArtboardToContent();
            // Fix bug #4: fetch webfonts referenced by the design, THEN render.
            loadFontsUsedInCanvas(this.canvas).finally(() => {
              this.canvas.requestRenderAll();
              this.viewport.fit();
              this.pushHistory();
              this.refreshLayers();
            });
          });
        };
        if (dims) this.setState({ canvasWidth: dims.w, canvasHeight: dims.h }, finishLoad);
        else finishLoad();
        return;
      } catch (e) {
        console.warn('Design JSON invalid, fallback la imagine plata:', e);
      }
    }

    if (this.props.initialImage) {
      if (this.props.sizeToImage) {
        this.loadProductImage(this.props.initialImage);
      } else {
        this.loadImageOnCanvas(this.props.initialImage, true).then((ok) => {
          this.viewport.fit();
          if (!ok) this.pushHistory();
        });
      }
    } else {
      this.viewport.fit();
      this.pushHistory();
    }
  };

  // Move any artboard background carried by a loaded design into React state and
  // clear canvas.backgroundColor (fabric would fill the whole pasteboard under
  // zoom — we paint the artboard-only fill in a render hook). Supports both the
  // new `artboardBg` key and legacy designs that set canvas.backgroundColor.
  _absorbBackground = (parsed) => {
    let bg = (parsed && typeof parsed.artboardBg === 'string') ? parsed.artboardBg : '';
    if (!bg && typeof this.canvas.backgroundColor === 'string') bg = this.canvas.backgroundColor;
    this.canvas.backgroundColor = null;
    this.setState({ bgColor: bg });
  };

  // Read the persisted artboard size off a loaded design (see exportDesignJson).
  // Returns { w, h } or null if the design predates the persisted-size fix.
  _artboardDimsFromDesign = (parsed) => {
    if (!parsed) return null;
    const w = Number(parsed.artboardWidth);
    const h = Number(parsed.artboardHeight);
    if (w > 0 && h > 0) return { w: Math.round(w), h: Math.round(h) };
    return null;
  };

  // Fallback for legacy designs with no persisted size: size the artboard to the
  // bounding box of the loaded objects (artboard origin is top-left, so width =
  // right edge, height = bottom edge). Runs inside the loadFromJSON callback, so
  // objects exist; uses absolute (viewport-independent) bounds.
  _sizeArtboardToContent = () => {
    const objs = this.canvas.getObjects();
    if (!objs.length) return;
    let maxX = 0;
    let maxY = 0;
    objs.forEach((o) => {
      const r = o.getBoundingRect(true);
      maxX = Math.max(maxX, r.left + r.width);
      maxY = Math.max(maxY, r.top + r.height);
    });
    const w = Math.max(1, Math.ceil(maxX));
    const h = Math.max(1, Math.ceil(maxY));
    this.setState({ canvasWidth: w, canvasHeight: h });
  };

  // ===========================================================================
  // History
  // ===========================================================================
  pushHistory = () => {
    // meta carries editor state that isn't in the fabric object graph: the
    // artboard bg (kept off canvas.backgroundColor — see history.js) and the
    // artboard size (fabric.toJSON omits canvas dims), so undo/redo of a resize
    // restores both.
    this.history.push(this._historyMeta());
    this._syncHistory();
  };

  _historyMeta = () => ({
    bgColor: this.state.bgColor,
    canvasWidth: this.state.canvasWidth,
    canvasHeight: this.state.canvasHeight,
  });

  pushHistoryDebounced = (label, ms = 400) => {
    clearTimeout(this._historyDebounceTimer);
    this._historyDebounceTimer = setTimeout(() => this.pushHistory(label), ms);
  };

  _syncHistory = () => {
    const canUndo = this.history.canUndo();
    const canRedo = this.history.canRedo();
    if (canUndo !== this.state.canUndo || canRedo !== this.state.canRedo) {
      this.setState({ canUndo, canRedo });
    }
    if (this.props.onDirtyChange) this.props.onDirtyChange(this.history.isDirty());
  };

  undo = () => {
    // Flush any pending debounced snapshot first so it isn't lost.
    if (this._historyDebounceTimer) { clearTimeout(this._historyDebounceTimer); this._historyDebounceTimer = null; this.history.push(this._historyMeta()); }
    const res = this.history.undo();
    if (res) this._applyHistoryEntry(res);
    this._syncHistory();
  };

  redo = () => {
    const res = this.history.redo();
    if (res) this._applyHistoryEntry(res);
    this._syncHistory();
  };

  _applyHistoryEntry = (res) => {
    const meta = res.meta || {};
    const patch = { bgColor: typeof meta.bgColor === 'string' ? meta.bgColor : '' };
    // Restore the artboard size captured with this snapshot so undo/redo across a
    // resize (and the initial baseline after loading a saved design) sizes the
    // artboard correctly — keeping the export crop rect in sync.
    if (meta.canvasWidth > 0 && meta.canvasHeight > 0) {
      patch.canvasWidth = meta.canvasWidth;
      patch.canvasHeight = meta.canvasHeight;
    }
    this.setState(patch);
    this._applySnapshot(res.json);
  };

  // Restore a snapshot and re-select the same objects by index (bug #11: undo
  // used to drop the selection and flash).
  _applySnapshot = (json) => {
    const prevActive = this.canvas.getActiveObject();
    let selIndices = [];
    if (prevActive) {
      if (prevActive.type === 'activeSelection') {
        selIndices = prevActive.getObjects().map((o) => this.canvas.getObjects().indexOf(o));
      } else {
        selIndices = [this.canvas.getObjects().indexOf(prevActive)];
      }
    }
    this.canvas.loadFromJSON(this._prepareJsonForLoad(json), () => {
      loadFontsUsedInCanvas(this.canvas).finally(() => {
        const objs = this.canvas.getObjects();
        const restored = selIndices.map((i) => objs[i]).filter(Boolean);
        if (restored.length === 1) {
          this.canvas.setActiveObject(restored[0]);
        } else if (restored.length > 1) {
          const sel = new fabric.ActiveSelection(restored, { canvas: this.canvas });
          this.canvas.setActiveObject(sel);
        }
        this.canvas.requestRenderAll();
        this.refreshLayers();
      });
    });
  };

  _prepareJsonForLoad = (jsonStr) => {
    try {
      const parsed = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
      if (parsed.objects) {
        const fix = (arr) => arr.forEach((obj) => {
          if (obj.type === 'image') obj.crossOrigin = 'anonymous';
          if (Array.isArray(obj.objects)) fix(obj.objects);
        });
        fix(parsed.objects);
      }
      return parsed;
    } catch (e) {
      return jsonStr;
    }
  };

  // ===========================================================================
  // Editor API implementations
  // ===========================================================================
  registerSerializationProps = (props) => {
    (props || []).forEach((p) => {
      if (!this._serializationProps.includes(p)) this._serializationProps.push(p);
    });
  };

  toast = (msg, type = 'info') => {
    this.setState({ toast: { msg, type } });
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => this.setState({ toast: null }), 6000);
  };

  registerCommand = (keySpec, fn, opts = {}) => {
    const specs = Array.isArray(keySpec) ? keySpec : [keySpec];
    const entry = { specs: specs.map((s) => s.toLowerCase()), fn, when: opts.when };
    this._commands.push(entry);
    return () => {
      const i = this._commands.indexOf(entry);
      if (i >= 0) this._commands.splice(i, 1);
    };
  };

  addToolButton = (def) => {
    this.setState((prev) => ({ extraToolButtons: [...prev.extraToolButtons, def] }));
    return () => this.setState((prev) => ({ extraToolButtons: prev.extraToolButtons.filter((d) => d !== def) }));
  };

  addPanelSection = (def) => {
    this.setState((prev) => ({
      extraPanelSections: [...prev.extraPanelSections, def].sort((a, b) => (a.order || 0) - (b.order || 0)),
    }));
    return () => this.setState((prev) => ({ extraPanelSections: prev.extraPanelSections.filter((d) => d !== def) }));
  };

  enterMode = (name, handlers = {}) => {
    if (this._mode) this.exitMode();
    this._mode = { name, handlers };
    if (handlers.onEnter) handlers.onEnter();
  };

  exitMode = (name) => {
    if (!this._mode) return;
    if (name && this._mode.name !== name) return;
    const { handlers } = this._mode;
    this._mode = null;
    if (handlers && handlers.onExit) handlers.onExit();
  };

  // Swap an image object for a new src, preserving on-canvas transform, z-order
  // and selection. Used by bg-removal, upscale and magic-erase.
  replaceImage = (obj, src, opts = {}, cb) => {
    const preserveDisplaySize = opts.preserveDisplaySize !== false;
    fabric.Image.fromURL(src, (newImg) => {
      if (!newImg || !newImg.width) { if (cb) cb(null); return; }
      const props = {
        left: obj.left, top: obj.top, angle: obj.angle,
        originX: obj.originX, originY: obj.originY,
        flipX: obj.flipX, flipY: obj.flipY,
        name: opts.name != null ? opts.name : obj.name,
      };
      if (preserveDisplaySize) {
        props.scaleX = obj.getScaledWidth() / newImg.width;
        props.scaleY = obj.getScaledHeight() / newImg.height;
      } else {
        props.scaleX = obj.scaleX; props.scaleY = obj.scaleY;
      }
      newImg.set(props);
      let idx = this.canvas.getObjects().indexOf(obj);
      if (idx < 0) idx = this.canvas.getObjects().length;
      this.canvas.remove(obj);
      this.canvas.insertAt(newImg, idx, false);
      this.canvas.setActiveObject(newImg);
      this.canvas.requestRenderAll();
      this.pushHistory(opts.label || 'Inlocuire imagine');
      this.refreshLayers();
      if (cb) cb(newImg);
    }, { crossOrigin: 'anonymous' });
  };

  // ===========================================================================
  // Keyboard dispatch (FOUNDATION-owned). Features add via registerCommand.
  // ===========================================================================
  _registerCoreCommands = () => {
    const R = this.registerCommand;
    R(['mod+z'], () => this.undo());
    R(['mod+shift+z', 'mod+y'], () => this.redo());
    R(['mod+=', 'mod+plus', 'mod+shift+='], () => this.viewport.zoomIn());
    R(['mod+-', 'mod+_'], () => this.viewport.zoomOut());
    R(['mod+0'], () => this.viewport.zoomActual());
    R(['mod+c'], () => this.copyObject());
    R(['mod+d'], () => this.duplicateObject());
    R(['mod+a'], () => this.selectAll());
    R(['delete', 'backspace'], () => { if (this.canvas.getActiveObject()) this.deleteSelected(); });
    R(['v'], () => this.setTool('select'));
    R(['t'], () => this.addText());
    R(['r'], () => this.addRect());
    R(['l'], () => this.addLine());
    // 'c' is intentionally left unbound for the crop-mode feature agent.
    R(['mod+]'], () => this.bringForward());
    R(['mod+['], () => this.sendBackward());
    R(['mod+alt+]'], () => this.bringToFront());
    R(['mod+alt+['], () => this.sendToBack());
    R(['arrowup', 'shift+arrowup'], (ed, e) => this.nudge(0, e.shiftKey ? -10 : -1));
    R(['arrowdown', 'shift+arrowdown'], (ed, e) => this.nudge(0, e.shiftKey ? 10 : 1));
    R(['arrowleft', 'shift+arrowleft'], (ed, e) => this.nudge(e.shiftKey ? -10 : -1, 0));
    R(['arrowright', 'shift+arrowright'], (ed, e) => this.nudge(e.shiftKey ? 10 : 1, 0));
  };

  _combo = (e) => {
    const parts = [];
    if (e.ctrlKey || e.metaKey) parts.push('mod');
    if (e.altKey) parts.push('alt');
    if (e.shiftKey) parts.push('shift');
    let key = (e.key || '').toLowerCase();
    if (key === ' ') key = 'space';
    else if (key === '+') key = 'plus';
    parts.push(key);
    return parts.join('+');
  };

  handleKeyDown = (e) => {
    // --- Escape: two-stage (bug #7) — handled before the typing guard so it
    //     works while editing an IText. Stage 1: exit editing/mode. Stage 2: ask
    //     the host to close (dirty guard lives there).
    if (e.key === 'Escape') {
      // Stage 1a: exit on-canvas text editing (fabric's hidden textarea).
      if (this.editor && this.editor.isEditingText()) {
        e.preventDefault();
        const o = this.canvas.getActiveObject();
        if (o && o.exitEditing) o.exitEditing();
        return;
      }
      // Stage 1b: a focused panel field just blurs (don't close mid-typing).
      const et = e.target;
      if (et && (et.tagName === 'INPUT' || et.tagName === 'TEXTAREA' || et.isContentEditable)) {
        et.blur();
        return;
      }
      // Stage 1c: exit an active canvas mode (crop, mask, ...).
      if (this._mode) { e.preventDefault(); this.exitMode(); return; }
      // Stage 2: ask the host to close (dirty guard lives there).
      if (this.props.onRequestClose) { e.preventDefault(); this.props.onRequestClose(); }
      return;
    }

    // Don't intercept while typing in a form field or editing text on canvas.
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (this.editor && this.editor.isEditingText()) return;

    // Native paste path: let the OS 'paste' event fire so clipboard images work.
    if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) {
      this._pasteTriggeredByKeyboard = true;
      return;
    }

    const combo = this._combo(e);
    let cmd = this._commands.find((c) => c.specs.includes(combo) && (!c.when || c.when()));
    // Allow a lone leading 'shift+' to fall through to the unshifted binding
    // (e.g. Shift+Arrow nudge, whose fn reads e.shiftKey itself).
    if (!cmd && combo.startsWith('shift+')) {
      const bare = combo.slice('shift+'.length);
      cmd = this._commands.find((c) => c.specs.includes(bare) && (!c.when || c.when()));
    }
    if (cmd) { e.preventDefault(); cmd.fn(this.editor, e); }
  };

  // ===========================================================================
  // Selection & property panel mirror
  // ===========================================================================
  handleSelection = () => {
    const obj = this.canvas.getActiveObject();
    if (!obj) { this.setState({ activeObject: null, activeType: null }); return; }
    const shadow = obj.shadow;
    this.setState({
      activeObject: obj,
      activeType: obj.type,
      fillColor: (typeof obj.fill === 'string' && obj.fill) || '#ffffff',
      strokeColor: obj.stroke || '#000000',
      opacity: obj.opacity != null ? obj.opacity : 1,
      fontSize: obj.fontSize || 32,
      fontFamily: obj.fontFamily || 'Arial',
      fontBold: obj.fontWeight === 'bold',
      fontItalic: obj.fontStyle === 'italic',
      shadowEnabled: !!shadow,
      shadowColor: (shadow && shadow.color) || '#000000',
      shadowBlur: shadow && shadow.blur != null ? shadow.blur : 10,
      shadowOffsetX: shadow && shadow.offsetX != null ? shadow.offsetX : 4,
      shadowOffsetY: shadow && shadow.offsetY != null ? shadow.offsetY : 4,
    });
  };

  _isTextType = (type) => type === 'i-text' || type === 'textbox' || type === 'text';

  // ===========================================================================
  // Tools
  // ===========================================================================
  setTool = (tool) => {
    this.setState({ activeTool: tool });
    this.canvas.isDrawingMode = tool === 'draw';
    if (tool === 'draw') {
      if (!this.canvas.freeDrawingBrush) this.canvas.freeDrawingBrush = new fabric.PencilBrush(this.canvas);
      this.canvas.freeDrawingBrush.color = this.state.strokeColor;
      this.canvas.freeDrawingBrush.width = 3;
    }
    this.canvas.selection = tool === 'select';
  };

  _centerOfArtboard = () => ({ x: this.state.canvasWidth / 2, y: this.state.canvasHeight / 2 });

  addText = () => {
    const c = this._centerOfArtboard();
    const text = new fabric.IText('Text', {
      left: c.x, top: c.y, originX: 'center', originY: 'center',
      fontFamily: this.state.fontFamily, fontSize: this.state.fontSize,
      fill: this.state.fillColor, name: 'Text',
    });
    this.canvas.add(text);
    this.canvas.setActiveObject(text);
    this.canvas.requestRenderAll();
    this.pushHistory('Text');
    this.setTool('select');
  };

  addRect = () => {
    const c = this._centerOfArtboard();
    const rect = new fabric.Rect({
      left: c.x, top: c.y, originX: 'center', originY: 'center',
      width: 100, height: 100, fill: this.state.fillColor,
      stroke: this.state.strokeColor, strokeWidth: 2, rx: 0, ry: 0, name: 'Dreptunghi',
    });
    this.canvas.add(rect);
    this.canvas.setActiveObject(rect);
    this.canvas.requestRenderAll();
    this.pushHistory('Dreptunghi');
    this.setTool('select');
  };

  addCircle = () => {
    const c = this._centerOfArtboard();
    const circle = new fabric.Circle({
      left: c.x, top: c.y, originX: 'center', originY: 'center',
      radius: 40, fill: this.state.fillColor, stroke: this.state.strokeColor,
      strokeWidth: 2, name: 'Cerc',
    });
    this.canvas.add(circle);
    this.canvas.setActiveObject(circle);
    this.canvas.requestRenderAll();
    this.pushHistory('Cerc');
    this.setTool('select');
  };

  addLine = () => {
    const { canvasWidth, canvasHeight } = this.state;
    const line = new fabric.Line([50, canvasHeight / 2, canvasWidth - 50, canvasHeight / 2], {
      stroke: this.state.strokeColor, strokeWidth: 3, name: 'Linie',
    });
    this.canvas.add(line);
    this.canvas.setActiveObject(line);
    this.canvas.requestRenderAll();
    this.pushHistory('Linie');
    this.setTool('select');
  };

  selectAll = () => {
    const objs = this.canvas.getObjects().filter((o) => o.selectable !== false);
    if (!objs.length) return;
    this.canvas.discardActiveObject();
    if (objs.length === 1) this.canvas.setActiveObject(objs[0]);
    else this.canvas.setActiveObject(new fabric.ActiveSelection(objs, { canvas: this.canvas }));
    this.canvas.requestRenderAll();
  };

  nudge = (dx, dy) => {
    const obj = this.canvas.getActiveObject();
    if (!obj) return;
    obj.set({ left: obj.left + dx, top: obj.top + dy });
    obj.setCoords();
    this.canvas.requestRenderAll();
    this.pushHistoryDebounced('Mutare');
  };

  // ===========================================================================
  // Image handling
  // ===========================================================================
  triggerImageUpload = () => this.fileInputRef.current.click();

  handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => this.loadImageOnCanvas(ev.target.result, false);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      Array.from(e.dataTransfer.files).forEach((file) => {
        if (!file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = (ev) => this.loadImageOnCanvas(ev.target.result, false);
        reader.readAsDataURL(file);
      });
      return;
    }
    const html = e.dataTransfer.getData('text/html');
    if (html) {
      const match = html.match(/<img[^>]+src="([^"]+)"/i);
      if (match && match[1]) { this.loadImageOnCanvas(match[1], false); return; }
    }
    const url = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('text/uri-list');
    if (url && /^https?:\/\/.+/i.test(url)) this.loadImageOnCanvas(url, false);
  };

  _toBase64 = async (src) => {
    if (!src || src.startsWith('data:')) return src;
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(src);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      try {
        return await new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth; c.height = img.naturalHeight;
            c.getContext('2d').drawImage(img, 0, 0);
            try { resolve(c.toDataURL('image/png')); } catch (_) { resolve(src); }
          };
          img.onerror = () => resolve(src);
          img.src = src;
        });
      } catch (_) { return src; }
    }
  };

  // Size the artboard (logical design size). No fabric element rebuild — the
  // fabric element tracks the container; only the exported region changes.
  _setArtboardSize = (w, h, cb) => {
    this.setState({ canvasWidth: w, canvasHeight: h }, () => { this.viewport.fit(); if (cb) cb(); });
  };

  loadProductImage = async (src) => {
    const safeSrc = await this._toBase64(src);
    return new Promise((resolve) => {
      fabric.Image.fromURL(safeSrc, (img) => {
        if (!img || !img.width) {
          this.toast('Imaginea nu a putut fi incarcata in editor.', 'error');
          this.pushHistory();
          resolve(false);
          return;
        }
        const natW = img.width, natH = img.height;
        const downscale = Math.min(1, MAX_EDIT_DIM / Math.max(natW, natH));
        const cw = Math.max(1, Math.round(natW * downscale));
        const ch = Math.max(1, Math.round(natH * downscale));
        img.set({
          originX: 'left', originY: 'top', left: 0, top: 0,
          scaleX: cw / natW, scaleY: ch / natH,
          name: 'Imagine produs', selectable: true,
        });
        this._setArtboardSize(cw, ch, () => {
          this.canvas.add(img);
          this.canvas.sendToBack(img);
          this.canvas.setActiveObject(img);
          this.canvas.requestRenderAll();
          this.pushHistory();
          this.refreshLayers();
          resolve(true);
        });
      }, { crossOrigin: 'anonymous' });
    });
  };

  loadImageOnCanvas = async (src, asBackground = false) => {
    const safeSrc = await this._toBase64(src);
    return new Promise((resolve) => {
      fabric.Image.fromURL(safeSrc, (img) => {
        if (!img || !img.width) {
          this.toast('Imaginea nu a putut fi incarcata in editor.', 'error');
          resolve(false);
          return;
        }
        const { canvasWidth, canvasHeight } = this.state;
        if (asBackground) {
          const scale = Math.max(canvasWidth / img.width, canvasHeight / img.height);
          img.set({
            scaleX: scale, scaleY: scale, left: canvasWidth / 2, top: canvasHeight / 2,
            originX: 'center', originY: 'center', selectable: true, name: 'Fundal',
          });
          this.canvas.add(img);
          this.canvas.sendToBack(img);
        } else {
          const scale = Math.min((canvasWidth * 0.6) / img.width, (canvasHeight * 0.6) / img.height, 1);
          img.set({
            scaleX: scale, scaleY: scale, left: canvasWidth / 2, top: canvasHeight / 2,
            originX: 'center', originY: 'center', name: 'Imagine',
          });
          this.canvas.add(img);
        }
        this.canvas.setActiveObject(img);
        this.canvas.requestRenderAll();
        this.pushHistory('Imagine');
        resolve(true);
      }, { crossOrigin: 'anonymous' });
    });
  };

  insertEventImage = (src) => {
    this.loadImageOnCanvas(src, false);
    this.setState({ showEventImages: false });
  };

  // ===========================================================================
  // Object manipulation
  // ===========================================================================
  deleteSelected = () => {
    const obj = this.canvas.getActiveObject();
    if (!obj) return;
    if (obj.type === 'activeSelection') {
      obj.forEachObject((o) => this.canvas.remove(o));
      this.canvas.discardActiveObject();
    } else {
      this.canvas.remove(obj);
    }
    this.canvas.requestRenderAll();
    this.pushHistory('Stergere');
  };

  handlePaste = (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') { this._pasteTriggeredByKeyboard = false; return; }
    const items = e.clipboardData && e.clipboardData.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (!blob) continue;
          const reader = new FileReader();
          reader.onload = (ev) => this.loadImageOnCanvas(ev.target.result, false);
          reader.readAsDataURL(blob);
          this._pasteTriggeredByKeyboard = false;
          return;
        }
      }
    }
    if (this._pasteTriggeredByKeyboard) { e.preventDefault(); this.pasteObject(); }
    this._pasteTriggeredByKeyboard = false;
  };

  copyObject = () => {
    const obj = this.canvas.getActiveObject();
    if (!obj) return;
    obj.clone((cloned) => { this._clipboard = cloned; }, this._serializationProps);
  };

  pasteObject = () => {
    if (!this._clipboard) return;
    this._clipboard.clone((cloned) => {
      this.canvas.discardActiveObject();
      cloned.set({ left: cloned.left + 20, top: cloned.top + 20, evented: true });
      if (cloned.type === 'activeSelection') {
        cloned.canvas = this.canvas;
        cloned.forEachObject((o) => this.canvas.add(o));
        cloned.setCoords();
      } else {
        this.canvas.add(cloned);
      }
      this._clipboard.top += 20;
      this._clipboard.left += 20;
      this.canvas.setActiveObject(cloned);
      this.canvas.requestRenderAll();
      this.pushHistory('Lipire');
    }, this._serializationProps);
  };

  // Bug #10: clean synchronous duplicate — clone directly, no setTimeout hack.
  duplicateObject = () => {
    const obj = this.canvas.getActiveObject();
    if (!obj) return;
    obj.clone((cloned) => {
      this.canvas.discardActiveObject();
      cloned.set({ left: obj.left + 20, top: obj.top + 20, evented: true });
      if (cloned.type === 'activeSelection') {
        cloned.canvas = this.canvas;
        cloned.forEachObject((o) => this.canvas.add(o));
        cloned.setCoords();
      } else {
        this.canvas.add(cloned);
      }
      this.canvas.setActiveObject(cloned);
      this.canvas.requestRenderAll();
      this.pushHistory('Dublare');
    }, this._serializationProps);
  };

  bringForward = () => { const o = this.canvas.getActiveObject(); if (o) { this.canvas.bringForward(o); this.canvas.requestRenderAll(); this.pushHistory('Ordine'); this.refreshLayers(); } };
  sendBackward = () => { const o = this.canvas.getActiveObject(); if (o) { this.canvas.sendBackwards(o); this.canvas.requestRenderAll(); this.pushHistory('Ordine'); this.refreshLayers(); } };
  bringToFront = () => { const o = this.canvas.getActiveObject(); if (o) { this.canvas.bringToFront(o); this.canvas.requestRenderAll(); this.pushHistory('Ordine'); this.refreshLayers(); } };
  sendToBack = () => { const o = this.canvas.getActiveObject(); if (o) { this.canvas.sendToBack(o); this.canvas.requestRenderAll(); this.pushHistory('Ordine'); this.refreshLayers(); } };

  // ===========================================================================
  // Property changes — every one routes a (debounced) history snapshot.
  // ===========================================================================
  handleFillChange = (e) => {
    const color = e.target.value;
    this.setState({ fillColor: color });
    const obj = this.canvas.getActiveObject();
    if (obj) { obj.set('fill', color); this.canvas.requestRenderAll(); this.pushHistoryDebounced('Culoare umplere'); }
  };

  handleStrokeChange = (e) => {
    const color = e.target.value;
    this.setState({ strokeColor: color });
    const obj = this.canvas.getActiveObject();
    if (obj) { obj.set('stroke', color); this.canvas.requestRenderAll(); this.pushHistoryDebounced('Culoare contur'); }
    if (this.canvas.freeDrawingBrush) this.canvas.freeDrawingBrush.color = color;
  };

  handleOpacityChange = (e) => {
    const val = parseFloat(e.target.value);
    this.setState({ opacity: val });
    const obj = this.canvas.getActiveObject();
    if (obj) { obj.set('opacity', val); this.canvas.requestRenderAll(); this.pushHistoryDebounced('Opacitate'); }
  };

  handleFontSizeChange = (e) => {
    const val = parseInt(e.target.value, 10);
    this.setState({ fontSize: val });
    const obj = this.canvas.getActiveObject();
    if (obj && this._isTextType(obj.type)) { obj.set('fontSize', val); this.canvas.requestRenderAll(); this.pushHistoryDebounced('Marime text'); }
  };

  handleFontFamilyChange = async (fontFamily) => {
    this.setState({ fontFamily, fontSearch: '', fontDropdownOpen: false });
    await loadGoogleFont(fontFamily);
    const obj = this.canvas.getActiveObject();
    if (obj && this._isTextType(obj.type)) { obj.set('fontFamily', fontFamily); this.canvas.requestRenderAll(); this.pushHistory('Font'); }
  };

  getFilteredFonts = () => {
    const q = this.state.fontSearch.toLowerCase();
    if (!q) return GOOGLE_FONTS.slice(0, 50);
    return GOOGLE_FONTS.filter((f) => f.toLowerCase().includes(q)).slice(0, 30);
  };

  toggleBold = () => {
    this.setState((prev) => {
      const bold = !prev.fontBold;
      const obj = this.canvas.getActiveObject();
      if (obj && this._isTextType(obj.type)) { obj.set('fontWeight', bold ? 'bold' : 'normal'); this.canvas.requestRenderAll(); this.pushHistory('Bold'); }
      return { fontBold: bold };
    });
  };

  toggleItalic = () => {
    this.setState((prev) => {
      const italic = !prev.fontItalic;
      const obj = this.canvas.getActiveObject();
      if (obj && this._isTextType(obj.type)) { obj.set('fontStyle', italic ? 'italic' : 'normal'); this.canvas.requestRenderAll(); this.pushHistory('Italic'); }
      return { fontItalic: italic };
    });
  };

  // ===========================================================================
  // Shadow / Glow
  // ===========================================================================
  _applyShadow = (updates = {}) => {
    const state = { ...this.state, ...updates };
    const obj = this.canvas.getActiveObject();
    if (!obj) return;
    if (!state.shadowEnabled) obj.set('shadow', null);
    else obj.set('shadow', new fabric.Shadow({
      color: state.shadowColor, blur: state.shadowBlur,
      offsetX: state.shadowOffsetX, offsetY: state.shadowOffsetY,
    }));
    this.canvas.requestRenderAll();
    this.pushHistoryDebounced('Umbra');
  };

  toggleShadow = () => {
    this.setState((prev) => {
      const enabled = !prev.shadowEnabled;
      const updates = { shadowEnabled: enabled };
      this._applyShadow(updates);
      return updates;
    });
  };

  handleShadowColor = (e) => { const color = e.target.value; this.setState({ shadowColor: color }, () => this._applyShadow()); };
  handleShadowBlur = (e) => { const blur = parseInt(e.target.value, 10); this.setState({ shadowBlur: blur }, () => this._applyShadow()); };
  handleShadowOffsetX = (e) => { const val = parseInt(e.target.value, 10); this.setState({ shadowOffsetX: val }, () => this._applyShadow()); };
  handleShadowOffsetY = (e) => { const val = parseInt(e.target.value, 10); this.setState({ shadowOffsetY: val }, () => this._applyShadow()); };

  applyGlowPreset = () => {
    this.setState({ shadowEnabled: true, shadowColor: '#00aaff', shadowBlur: 20, shadowOffsetX: 0, shadowOffsetY: 0 }, () => this._applyShadow());
  };

  handleBgColorChange = (e) => {
    const color = e.target.value;
    this.setState({ bgColor: color }, () => { this.canvas.requestRenderAll(); this.pushHistoryDebounced('Fundal'); });
  };

  clearBgColor = () => {
    this.setState({ bgColor: '' }, () => { this.canvas.requestRenderAll(); this.pushHistory('Fundal transparent'); });
  };

  // ===========================================================================
  // Canvas size (artboard). Debounced (bug #3) so per-keystroke typing in the
  // W/H inputs doesn't thrash.
  // ===========================================================================
  handlePresetChange = (e) => {
    const idx = parseInt(e.target.value, 10);
    const preset = PRESETS[idx];
    if (preset.w && preset.h) this.resizeCanvas(preset.w, preset.h);
  };

  resizeCanvas = (w, h) => {
    this.setState({ canvasWidth: w, canvasHeight: h }, () => {
      this.canvas.requestRenderAll();
      this.viewport.fit();
      this.pushHistory('Dimensiune panza');
    });
  };

  resizeCanvasDebounced = (w, h) => {
    this.setState({ canvasWidth: w, canvasHeight: h });
    clearTimeout(this._resizeDebounceTimer);
    this._resizeDebounceTimer = setTimeout(() => {
      this.canvas.requestRenderAll();
      this.viewport.fit();
      this.pushHistory('Dimensiune panza');
    }, 350);
  };

  // ===========================================================================
  // Layers
  // ===========================================================================
  refreshLayers = () => {
    const objects = this.canvas.getObjects();
    const layers = objects.map((obj, i) => ({
      index: i,
      name: obj.name || obj.type || `Strat ${i}`,
      visible: obj.visible !== false,
      type: obj.type,
    })).reverse();
    this.setState({ layers });
  };

  toggleLayerVisibility = (idx) => {
    const obj = this.canvas.item(idx);
    if (obj) { obj.visible = !obj.visible; this.canvas.requestRenderAll(); this.pushHistory('Vizibilitate strat'); this.refreshLayers(); }
  };

  selectLayer = (idx) => {
    const obj = this.canvas.item(idx);
    if (obj) { this.canvas.setActiveObject(obj); this.canvas.requestRenderAll(); }
  };

  // ===========================================================================
  // Align
  // ===========================================================================
  alignObject = (alignment) => {
    const obj = this.canvas.getActiveObject();
    if (!obj) return;
    const { canvasWidth, canvasHeight } = this.state;
    const topLeft = obj.getPointByOrigin('left', 'top');
    obj.set({ originX: 'left', originY: 'top', left: topLeft.x, top: topLeft.y });
    const w = obj.getScaledWidth();
    const h = obj.getScaledHeight();
    switch (alignment) {
      case 'left': obj.set({ left: 0 }); break;
      case 'center-h': obj.set({ left: (canvasWidth - w) / 2 }); break;
      case 'right': obj.set({ left: canvasWidth - w }); break;
      case 'top': obj.set({ top: 0 }); break;
      case 'center-v': obj.set({ top: (canvasHeight - h) / 2 }); break;
      case 'bottom': obj.set({ top: canvasHeight - h }); break;
      case 'center': obj.set({ left: (canvasWidth - w) / 2, top: (canvasHeight - h) / 2 }); break;
      default: break;
    }
    obj.setCoords();
    this.canvas.requestRenderAll();
    this.pushHistory('Aliniere');
  };

  // ===========================================================================
  // Export — reset zoom+pan to identity, crop exactly to the artboard rect at
  // the chosen multiplier, restore. (See report for the crop-math proof.)
  // ===========================================================================
  exportAsImage = (format = 'png', multiplier = 1) => {
    const { canvasWidth, canvasHeight } = this.state;
    const savedVpt = this.canvas.viewportTransform.slice();
    const active = this.canvas.getActiveObject();
    this.canvas.discardActiveObject();
    this._exporting = true;
    // Identity viewport: object-space (0,0)-(W,H) maps 1:1 to canvas pixels.
    this.canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
    let dataURL;
    try {
      dataURL = this.canvas.toDataURL({
        format,
        quality: format === 'jpeg' ? 0.92 : 1,
        left: 0, top: 0,
        width: canvasWidth, height: canvasHeight,
        multiplier,
      });
    } finally {
      this._exporting = false;
      this.canvas.viewportTransform = savedVpt;
      if (active) this.canvas.setActiveObject(active);
      this.canvas.requestRenderAll();
    }
    return dataURL;
  };

  exportDesignJson = () => {
    const json = this.canvas.toJSON(this._serializationProps);
    const canvasObjects = this.canvas.getObjects();
    const embed = (objs, fabricObjs) => {
      objs.forEach((obj, i) => {
        const fabricObj = fabricObjs[i];
        if (obj.type === 'image' && obj.src && !obj.src.startsWith('data:')) {
          try {
            if (fabricObj && fabricObj._element) {
              const el = fabricObj._element;
              const c = document.createElement('canvas');
              c.width = el.naturalWidth || el.width;
              c.height = el.naturalHeight || el.height;
              c.getContext('2d').drawImage(el, 0, 0);
              obj.src = c.toDataURL('image/png');
            }
          } catch (e) { /* leave remote src if tainted */ }
        }
        if (Array.isArray(obj.objects) && fabricObj && fabricObj._objects) embed(obj.objects, fabricObj._objects);
      });
    };
    if (json.objects) embed(json.objects, canvasObjects);
    // Persist the artboard background out-of-band (kept off canvas.backgroundColor
    // so fabric doesn't fill the whole pasteboard under zoom). Read back on load
    // by _absorbBackground.
    json.artboardBg = this.state.bgColor || null;
    // Persist the artboard SIZE out-of-band too: fabric.toJSON does not serialize
    // canvas dimensions, so without this a re-opened design falls back to the
    // preset default (400x400) and export crops the image to the wrong size —
    // silent data loss on the products round-trip. Read back by _loadInitialContent.
    json.artboardWidth = this.state.canvasWidth;
    json.artboardHeight = this.state.canvasHeight;
    return JSON.stringify(json);
  };

  handleExport = () => {
    try {
      const dataURL = this.exportAsImage('png');
      const designJson = this.exportDesignJson();
      if (this.props.onExport) this.props.onExport(dataURL, designJson);
    } catch (e) {
      this.toast('Exportul a esuat. O imagine din editor nu permite editarea (CORS).', 'error');
    }
  };

  handleDownload = () => {
    try {
      const dataURL = this.exportAsImage('png');
      const a = document.createElement('a');
      a.href = dataURL; a.download = 'design.png'; a.click();
    } catch (e) {
      this.toast('Descarcarea a esuat. O imagine din editor nu permite editarea (CORS).', 'error');
    }
  };

  // ===========================================================================
  // Background removal (client-side AI) — capped, progress, Romanian errors.
  // ===========================================================================
  removeBackground = async () => {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== 'image') return;
    this.setState({ removingBg: true, removingBgProgress: null });
    try {
      const src = obj.getSrc();
      const res = await fetch(src, src.startsWith('data:') ? undefined : { mode: 'cors' });
      const blob = await res.blob();
      const resultBlob = await removeImageBackground({
        blob,
        quality: this.state.bgQuality,
        onProgress: ({ phase, pct }) => this.setState({
          removingBgProgress: phase === 'download' ? `Descarcare model ${pct}%` : `Procesare ${pct}%`,
        }),
      });
      const dataUrl = await this._blobToDataUrl(resultBlob);
      this.replaceImage(obj, dataUrl, { name: (obj.name || 'Imagine') + ' (fara fundal)', label: 'Eliminare fundal' }, () => {
        this.setState({ removingBg: false, removingBgProgress: null });
      });
    } catch (err) {
      console.error('Eliminarea fundalului a esuat:', err);
      this.setState({ removingBg: false, removingBgProgress: null });
      this.toast('Eliminarea fundalului a esuat. Incearca alta calitate sau alta imagine.', 'error');
    }
  };

  _blobToDataUrl = (blob) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

  handleBgQualityChange = (e) => this.setState({ bgQuality: e.target.value }, () => preloadModel(this.state.bgQuality));

  // ===========================================================================
  // Magic upscale
  // ===========================================================================
  magicUpscale = async (factor) => {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== 'image') return;
    this.setState({ upscaling: true, showUpscaleMenu: false });
    try {
      const el = obj._element;
      await new Promise((r) => setTimeout(r, 10));
      const { url } = upscaleImageElement(el, factor);
      this.replaceImage(obj, url, { name: (obj.name || 'Imagine') + ` (${factor}x)`, label: 'Marire rezolutie' }, () => {
        this.setState({ upscaling: false });
      });
    } catch (err) {
      console.error('Marirea rezolutiei a esuat:', err);
      this.setState({ upscaling: false });
      this.toast('Marirea rezolutiei a esuat.', 'error');
    }
  };

  // ===========================================================================
  // Magic Erase (paint-to-remove inpainting) — a canvas MODE (one owner).
  // ===========================================================================
  enterMagicErase = () => {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== 'image') return;
    this._magicEraseTarget = obj;
    this._magicEraseStrokes = [];
    this.canvas.getObjects().forEach((o) => {
      o._prevSelectable = o.selectable;
      o.selectable = false; o.evented = false;
    });
    this.canvas.isDrawingMode = true;
    this.canvas.freeDrawingBrush = new fabric.PencilBrush(this.canvas);
    this.canvas.freeDrawingBrush.color = 'rgba(255, 60, 60, 0.45)';
    this.canvas.freeDrawingBrush.width = this.state.magicEraseBrushSize;
    this._magicErasePathHandler = (e) => {
      if (e.path) {
        e.path._isMagicErase = true;
        e.path.selectable = false; e.path.evented = false;
        this._magicEraseStrokes.push(e.path);
      }
    };
    this.canvas.on('path:created', this._magicErasePathHandler);
    this.enterMode('magicErase', { onExit: this._teardownMagicEraseMode });
    this.setState({ magicEraseMode: true });
  };

  cancelMagicErase = () => {
    if (this._magicEraseStrokes) this._magicEraseStrokes.forEach((s) => this.canvas.remove(s));
    this.exitMode('magicErase');
  };

  _teardownMagicEraseMode = () => {
    this.canvas.isDrawingMode = false;
    if (this._magicErasePathHandler) this.canvas.off('path:created', this._magicErasePathHandler);
    this.canvas.getObjects().forEach((o) => {
      if (o._prevSelectable !== undefined) { o.selectable = o._prevSelectable; o.evented = true; delete o._prevSelectable; }
    });
    this._magicEraseTarget = null;
    this._magicEraseStrokes = [];
    this.canvas.requestRenderAll();
    this.setState({ magicEraseMode: false, magicEraseApplying: false });
  };

  handleMagicEraseBrushSize = (e) => {
    const size = parseInt(e.target.value, 10);
    this.setState({ magicEraseBrushSize: size });
    if (this.canvas.freeDrawingBrush) this.canvas.freeDrawingBrush.width = size;
  };

  applyMagicErase = async () => {
    const img = this._magicEraseTarget;
    const strokes = this._magicEraseStrokes;
    if (!img || !strokes.length) return;
    this.setState({ magicEraseApplying: true });
    try {
      const imgEl = img._element;
      const natW = imgEl.naturalWidth || imgEl.width;
      const natH = imgEl.naturalHeight || imgEl.height;
      const imgCanvas = document.createElement('canvas');
      imgCanvas.width = natW; imgCanvas.height = natH;
      const imgCtx = imgCanvas.getContext('2d');
      imgCtx.drawImage(imgEl, 0, 0, natW, natH);

      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = natW; maskCanvas.height = natH;
      const maskCtx = maskCanvas.getContext('2d');
      maskCtx.fillStyle = 'black'; maskCtx.fillRect(0, 0, natW, natH);

      // Map brush strokes (recorded in scene/object space) into SOURCE-pixel space
      // through the image's own inverse transform — NOT the axis-aligned bounding
      // rect, which skews/offsets the mask for any rotated (angle !== 0) or
      // non-uniformly-scaled image. This mirrors cropMode.js's S2O approach:
      //   S2O = M . T,  T = translate(-(cropX + width/2), -(cropY + height/2)),
      //   M   = img.calcTransformMatrix()  (object-local -> scene, incl. scale+angle)
      // invS2O then maps a scene point straight to natural source pixels.
      const cropX0 = img.cropX || 0;
      const cropY0 = img.cropY || 0;
      const w0 = img.width;
      const h0 = img.height;
      const T = [1, 0, 0, 1, -(cropX0 + w0 / 2), -(cropY0 + h0 / 2)];
      const M = img.calcTransformMatrix();
      const S2O = fabric.util.multiplyTransformMatrices(M, T);
      const invS2O = fabric.util.invertTransform(S2O);
      const toSrc = (x, y) => fabric.util.transformPoint({ x, y }, invS2O);
      // Stroke width is uniform in scene units; convert to source px by the mean
      // inverse scale (source px per scene unit). Reduces to 1/scale for uniform
      // images — matching the previous width math for the un-rotated case.
      const sX = img.scaleX || 1;
      const sY = img.scaleY || 1;
      const widthScale = (1 / sX + 1 / sY) / 2;
      maskCtx.strokeStyle = 'white'; maskCtx.lineJoin = 'round'; maskCtx.lineCap = 'round';
      strokes.forEach((pathObj) => {
        const cmds = pathObj.path;
        if (!cmds) return;
        maskCtx.lineWidth = (pathObj.strokeWidth || 30) * widthScale;
        maskCtx.beginPath();
        cmds.forEach((cmd) => {
          let p;
          let c;
          switch (cmd[0]) {
            case 'M': p = toSrc(cmd[1], cmd[2]); maskCtx.moveTo(p.x, p.y); break;
            case 'Q': c = toSrc(cmd[1], cmd[2]); p = toSrc(cmd[3], cmd[4]); maskCtx.quadraticCurveTo(c.x, c.y, p.x, p.y); break;
            case 'L': p = toSrc(cmd[1], cmd[2]); maskCtx.lineTo(p.x, p.y); break;
            default: break;
          }
        });
        maskCtx.stroke();
      });

      const imageData = imgCtx.getImageData(0, 0, natW, natH);
      const maskData = maskCtx.getImageData(0, 0, natW, natH);
      await new Promise((resolve) => setTimeout(resolve, 10));
      inpaint(imageData, maskData);
      imgCtx.putImageData(imageData, 0, 0);
      const resultUrl = imgCanvas.toDataURL('image/png');

      strokes.forEach((s) => this.canvas.remove(s));
      this.replaceImage(img, resultUrl, { name: (img.name || 'Imagine') + ' (retusata)', label: 'Stergere magica' }, () => {
        this.exitMode('magicErase');
      });
    } catch (err) {
      console.error('Stergerea magica a esuat:', err);
      this.toast('Stergerea magica a esuat.', 'error');
      this.exitMode('magicErase');
    }
  };

  // ===========================================================================
  // Render
  // ===========================================================================
  render() {
    const {
      activeTool, activeObject, activeType, fillColor, strokeColor, opacity,
      fontSize, fontFamily, fontBold, fontItalic, fontSearch, fontDropdownOpen,
      canvasWidth, canvasHeight, zoomPct, layers, showEventImages,
      shadowEnabled, shadowColor, shadowBlur, shadowOffsetX, shadowOffsetY,
      toast, canUndo, canRedo, removingBg, removingBgProgress, bgQuality,
      magicEraseMode, magicEraseApplying, magicEraseBrushSize,
      extraToolButtons, extraPanelSections,
    } = this.state;
    const eventImages = this.props.eventImages || [];
    const isText = this._isTextType(activeType);
    const isImage = activeType === 'image';
    const filteredFonts = isText ? this.getFilteredFonts() : [];
    const fullScreen = !!this.props.fullScreen;

    return (
      <div className={`de-editor${fullScreen ? ' de-editor-fullscreen' : ''}`} ref={this.editorRef}>
        {toast && (
          <div className={`de-toast de-toast-${toast.type}`} onClick={() => this.setState({ toast: null })}>
            {toast.type === 'error' ? '⚠ ' : ''}{toast.msg}
          </div>
        )}

        {/* Top toolbar */}
        <div className="de-toolbar">
          <div className="de-toolbar-group">
            <button className={`de-btn ${activeTool === 'select' ? 'active' : ''}`} onClick={() => this.setTool('select')} title="Selecteaza (V)">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M7 2l10 10-4 1 3 7-2 1-3-7-4 4z" fill="currentColor"/></svg>
            </button>
          </div>
          <div className="de-toolbar-sep" />
          <div className="de-toolbar-group">
            <button className="de-btn" onClick={this.addText} title="Adauga text (T)">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M5 4v3h5.5v12h3V7H19V4z" fill="currentColor"/></svg>
            </button>
            <button className="de-btn" onClick={this.addRect} title="Dreptunghi (R)">
              <svg viewBox="0 0 24 24" width="18" height="18"><rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/></svg>
            </button>
            <button className="de-btn" onClick={this.addCircle} title="Cerc">
              <svg viewBox="0 0 24 24" width="18" height="18"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/></svg>
            </button>
            <button className="de-btn" onClick={this.addLine} title="Linie (L)">
              <svg viewBox="0 0 24 24" width="18" height="18"><line x1="5" y1="19" x2="19" y2="5" stroke="currentColor" strokeWidth="2"/></svg>
            </button>
            <button className={`de-btn ${activeTool === 'draw' ? 'active' : ''}`} onClick={() => this.setTool('draw')} title="Desen liber">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75z" fill="currentColor"/></svg>
            </button>
          </div>
          <div className="de-toolbar-sep" />
          <div className="de-toolbar-group">
            <button className="de-btn" onClick={this.triggerImageUpload} title="Adauga imagine">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M21 19V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2zM8.5 13.5l2.5 3 3.5-4.5 4.5 6H5l3.5-4.5z" fill="currentColor"/></svg>
            </button>
            <input ref={this.fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={this.handleImageUpload} />
          </div>

          {eventImages.length > 0 && (
            <div className="de-toolbar-group de-dropdown-wrap">
              <button className="de-btn" onClick={() => this.setState((p) => ({ showEventImages: !p.showEventImages }))} title="Insereaza imagine eveniment">
                Eveniment
              </button>
              {showEventImages && (
                <div className="de-dropdown">
                  {eventImages.map((img, i) => (img.src ? (
                    <button key={i} className="de-dropdown-item" onClick={() => this.insertEventImage(img.src)}>{img.name}</button>
                  ) : null))}
                </div>
              )}
            </div>
          )}

          <div className="de-toolbar-sep" />
          <div className="de-toolbar-group">
            <button className="de-btn" onClick={this.undo} disabled={!canUndo} title="Anuleaza (Ctrl+Z)">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M12.5 8c-2.65 0-5.05 1.04-6.83 2.72L3 8v9h9l-2.83-2.83C10.51 12.83 11.91 12.25 13.5 12.25c2.65 0 5.05 1.04 6.83 2.72l2.95-2.95C21.12 9.47 17.05 8 12.5 8z" fill="currentColor"/></svg>
            </button>
            <button className="de-btn" onClick={this.redo} disabled={!canRedo} title="Refa (Ctrl+Shift+Z)">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16a8.003 8.003 0 017.6-5.5c1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" fill="currentColor"/></svg>
            </button>
          </div>
          <div className="de-toolbar-sep" />
          <div className="de-toolbar-group">
            <button className="de-btn" onClick={this.deleteSelected} disabled={!activeObject} title="Sterge (Del)">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M6 19a2 2 0 002 2h8a2 2 0 002-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/></svg>
            </button>
            <button className="de-btn" onClick={this.duplicateObject} disabled={!activeObject} title="Dubleaza (Ctrl+D)">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M16 1H4a2 2 0 00-2 2v14h2V3h12V1zm3 4H8a2 2 0 00-2 2v14a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2zm0 16H8V7h11v14z" fill="currentColor"/></svg>
            </button>
            {isImage && !magicEraseMode && (
              <>
                <select className="de-select de-inline-select" value={bgQuality} onChange={this.handleBgQualityChange} title="Calitate eliminare fundal" disabled={removingBg}>
                  {Object.keys(BG_QUALITY).map((k) => <option key={k} value={k}>{BG_QUALITY[k].label}</option>)}
                </select>
                <button className="de-btn" onClick={this.removeBackground} disabled={removingBg} title="Elimina fundalul (AI)">
                  {removingBg ? (removingBgProgress || 'Se incarca modelul...') : 'Elimina fundal'}
                </button>
                <button className="de-btn" onClick={this.enterMagicErase} title="Sterge magic - picteaza zona de eliminat">
                  Sterge magic
                </button>
                <div className="de-dropdown-wrap" style={{ position: 'relative', display: 'inline-block' }}>
                  <button className="de-btn" disabled={this.state.upscaling} onClick={() => this.setState((p) => ({ showUpscaleMenu: !p.showUpscaleMenu }))} title="Mareste rezolutia">
                    {this.state.upscaling ? 'Se mareste...' : 'Mareste'}
                  </button>
                  {this.state.showUpscaleMenu && (
                    <div className="de-dropdown">
                      {[2, 4, 6, 8].map((f) => (
                        <button key={f} className="de-dropdown-item" onClick={() => this.magicUpscale(f)}>{f}x</button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {magicEraseMode && (
            <div className="de-toolbar-group de-magic-erase-bar">
              <span className="de-magic-erase-label">Picteaza zona de sters</span>
              <label className="de-label">Marime</label>
              <input type="range" min="5" max="100" step="1" value={magicEraseBrushSize} onChange={this.handleMagicEraseBrushSize} className="de-range" style={{ width: 80 }} />
              <span className="de-val">{magicEraseBrushSize}</span>
              <button className="de-btn de-btn-primary" onClick={this.applyMagicErase} disabled={magicEraseApplying}>{magicEraseApplying ? 'Se aplica...' : 'Aplica'}</button>
              <button className="de-btn" onClick={this.cancelMagicErase} disabled={magicEraseApplying}>Renunta</button>
            </div>
          )}

          <div className="de-toolbar-sep" />
          <div className="de-toolbar-group">
            <button className="de-btn" onClick={this.bringForward} disabled={!activeObject} title="Adu mai in fata">&#8593;</button>
            <button className="de-btn" onClick={this.sendBackward} disabled={!activeObject} title="Trimite mai in spate">&#8595;</button>
            <button className="de-btn" onClick={this.bringToFront} disabled={!activeObject} title="Adu in fata">&#10514;</button>
            <button className="de-btn" onClick={this.sendToBack} disabled={!activeObject} title="Trimite in spate">&#10515;</button>
          </div>
          <div className="de-toolbar-sep" />
          <div className="de-toolbar-group">
            <button className="de-btn" onClick={() => this.alignObject('left')} disabled={!activeObject} title="Aliniaza stanga">&#10999;</button>
            <button className="de-btn" onClick={() => this.alignObject('center-h')} disabled={!activeObject} title="Centreaza orizontal">&#11007;</button>
            <button className="de-btn" onClick={() => this.alignObject('right')} disabled={!activeObject} title="Aliniaza dreapta">&#11000;</button>
            <button className="de-btn" onClick={() => this.alignObject('top')} disabled={!activeObject} title="Aliniaza sus">&#11040;</button>
            <button className="de-btn" onClick={() => this.alignObject('center-v')} disabled={!activeObject} title="Centreaza vertical">&#11045;</button>
            <button className="de-btn" onClick={() => this.alignObject('bottom')} disabled={!activeObject} title="Aliniaza jos">&#11041;</button>
            <button className="de-btn" onClick={() => this.alignObject('center')} disabled={!activeObject} title="Centreaza pe panza">&#9678;</button>
          </div>

          {/* feature-registered tool buttons */}
          {extraToolButtons.length > 0 && (
            <>
              <div className="de-toolbar-sep" />
              <div className="de-toolbar-group">
                {extraToolButtons.map((b) => (
                  <button key={b.id} className={`de-btn ${b.isActive && b.isActive() ? 'active' : ''}`} onClick={() => b.onClick(this.editor)} title={b.title}>
                    {b.icon || b.title}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="de-toolbar-spacer" />
          {!fullScreen && (
            <div className="de-toolbar-group">
              <button className="de-btn de-btn-primary" onClick={this.handleExport} title="Foloseste imaginea">Foloseste</button>
              <button className="de-btn" onClick={this.handleDownload} title="Descarca PNG">Descarca</button>
            </div>
          )}
        </div>

        {/* Main area */}
        <div className="de-main">
          {/* Left: properties panel */}
          <div className="de-panel de-panel-left">
            <div className="de-panel-section">
              <div className="de-panel-title">Panza</div>
              <select className="de-select" value="" onChange={this.handlePresetChange}>
                <option value="" disabled>Alege dimensiune...</option>
                {PRESETS.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
              </select>
              <div className="de-row">
                <label className="de-label">L</label>
                <input type="number" className="de-input-sm" value={canvasWidth}
                  onChange={(e) => this.resizeCanvasDebounced(parseInt(e.target.value, 10) || 100, canvasHeight)} />
                <label className="de-label">I</label>
                <input type="number" className="de-input-sm" value={canvasHeight}
                  onChange={(e) => this.resizeCanvasDebounced(canvasWidth, parseInt(e.target.value, 10) || 100)} />
              </div>
              <div className="de-row">
                <label className="de-label">Fundal</label>
                <input type="color" className="de-color" value={this.state.bgColor || '#ffffff'} onChange={this.handleBgColorChange} />
                <button className={`de-btn-sm ${!this.state.bgColor ? 'active' : ''}`} onClick={this.clearBgColor} title="Fundal transparent">&#8856;</button>
              </div>
            </div>

            <div className="de-panel-section">
              <div className="de-panel-title">Culori</div>
              <div className="de-row">
                <label className="de-label">Umplere</label>
                <input type="color" className="de-color" value={fillColor} onChange={this.handleFillChange} />
              </div>
              <div className="de-row">
                <label className="de-label">Contur</label>
                <input type="color" className="de-color" value={strokeColor} onChange={this.handleStrokeChange} />
              </div>
              <div className="de-row">
                <label className="de-label">Opacitate</label>
                <input type="range" min="0" max="1" step="0.05" value={opacity} onChange={this.handleOpacityChange} className="de-range" />
              </div>
            </div>

            {activeObject && (
              <div className="de-panel-section">
                <div className="de-panel-title">
                  Umbra / Stralucire
                  <button className={`de-btn-sm ${shadowEnabled ? 'active' : ''}`} onClick={this.toggleShadow} style={{ marginLeft: 'auto', fontSize: 11 }}>
                    {shadowEnabled ? 'PORNIT' : 'OPRIT'}
                  </button>
                </div>
                {shadowEnabled && (
                  <>
                    <div className="de-row">
                      <label className="de-label">Culoare</label>
                      <input type="color" className="de-color" value={shadowColor} onChange={this.handleShadowColor} />
                    </div>
                    <div className="de-row">
                      <label className="de-label">Blur</label>
                      <input type="range" min="0" max="60" step="1" value={shadowBlur} onChange={this.handleShadowBlur} className="de-range" />
                      <span className="de-val">{shadowBlur}</span>
                    </div>
                    <div className="de-row">
                      <label className="de-label">X</label>
                      <input type="range" min="-50" max="50" step="1" value={shadowOffsetX} onChange={this.handleShadowOffsetX} className="de-range" />
                      <span className="de-val">{shadowOffsetX}</span>
                    </div>
                    <div className="de-row">
                      <label className="de-label">Y</label>
                      <input type="range" min="-50" max="50" step="1" value={shadowOffsetY} onChange={this.handleShadowOffsetY} className="de-range" />
                      <span className="de-val">{shadowOffsetY}</span>
                    </div>
                    <div className="de-row">
                      <button className="de-btn-sm" onClick={this.applyGlowPreset} title="Aplica stralucire neon">Stralucire</button>
                      <button className="de-btn-sm" onClick={() => this.setState({ shadowColor: '#000000', shadowBlur: 10, shadowOffsetX: 4, shadowOffsetY: 4 }, () => this._applyShadow())} title="Aplica umbra">Umbra</button>
                    </div>
                  </>
                )}
              </div>
            )}

            {isText && (
              <div className="de-panel-section">
                <div className="de-panel-title">Text</div>
                <div className="de-font-picker" ref={this.fontPickerRef}>
                  <input type="text" className="de-select" placeholder="Cauta fonturi..."
                    value={fontDropdownOpen ? fontSearch : fontFamily}
                    onFocus={() => { this.setState({ fontDropdownOpen: true, fontSearch: '' }); this.getFilteredFonts().forEach((f) => loadGoogleFont(f)); }}
                    onChange={(e) => this.setState({ fontSearch: e.target.value }, () => this.getFilteredFonts().forEach((f) => loadGoogleFont(f)))}
                    onBlur={() => setTimeout(() => this.setState({ fontDropdownOpen: false }), 200)} />
                  {fontDropdownOpen && (
                    <div className="de-font-dropdown">
                      {filteredFonts.map((f) => (
                        <button key={f} className={`de-font-option ${f === fontFamily ? 'active' : ''}`} style={{ fontFamily: `"${f}", sans-serif` }}
                          onMouseDown={(e) => { e.preventDefault(); this.handleFontFamilyChange(f); }}>{f}</button>
                      ))}
                      {filteredFonts.length === 0 && <div className="de-font-empty">Niciun font gasit</div>}
                    </div>
                  )}
                </div>
                <div className="de-row">
                  <label className="de-label">Marime</label>
                  <input type="number" className="de-input-sm" value={fontSize} onChange={this.handleFontSizeChange} min="8" max="200" />
                  <button className={`de-btn-sm ${fontBold ? 'active' : ''}`} onClick={this.toggleBold}><b>B</b></button>
                  <button className={`de-btn-sm ${fontItalic ? 'active' : ''}`} onClick={this.toggleItalic}><i>I</i></button>
                </div>
              </div>
            )}

            {/* feature-registered contextual panel sections */}
            {extraPanelSections.filter((s) => !s.when || s.when(activeObject)).map((s) => (
              <div className="de-panel-section" key={s.id}>
                {s.title && <div className="de-panel-title">{s.title}</div>}
                {s.render(this.editor)}
              </div>
            ))}
          </div>

          {/* Center: canvas (fabric fills this element; zoom lives on viewportTransform) */}
          <div className="de-canvas-area" ref={this.canvasAreaRef}>
            <canvas ref={this.canvasRef} />
            <div className="de-canvas-info">
              {canvasWidth} x {canvasHeight}px &mdash; {zoomPct}%
              {isImage && activeObject && activeObject._element && (
                <span style={{ marginLeft: 12 }}>
                  | Imagine: {activeObject._element.naturalWidth || activeObject._element.width} x {activeObject._element.naturalHeight || activeObject._element.height}px
                </span>
              )}
            </div>
            <div className="de-zoom-bar">
              <button className="de-btn" onClick={() => this.viewport.zoomOut()} title="Micsoreaza (Ctrl -)">&minus;</button>
              <span className="de-zoom-level">{zoomPct}%</span>
              <button className="de-btn" onClick={() => this.viewport.zoomIn()} title="Mareste (Ctrl +)">+</button>
              <div className="de-toolbar-sep" />
              <button className="de-btn" onClick={() => this.viewport.zoomActual()} title="Zoom 100% (Ctrl 0)">100%</button>
              <button className="de-btn" onClick={() => this.viewport.fit()} title="Potriveste in ecran">Potriveste</button>
              <button className="de-btn" onClick={() => this.viewport.center()} title="Centreaza vizualizarea">&#9678;</button>
            </div>
          </div>
          {/* The layers panel is now registered by the LayersPanel feature as a
              left-column panel section (drag-reorder/rename/lock/group). The old
              hardcoded right-column Straturi list was removed to avoid a duplicate
              panel; refreshLayers()/state.layers still drive the feature re-render. */}
        </div>
      </div>
    );
  }
}

export default DesignEditor;
